import {
  Alert,
  Checkbox,
  Input,
  Modal,
  Space,
  Typography,
  notification,
} from "antd";

import { Form, Formik } from "formik";
import React, { useRef, useState } from "react";
import * as Yup from "yup";

import { createUser, updateUserById } from "../../store/api/users";

const { Title, Text } = Typography;

const AddUserPopup = ({
  closePopup,
  item,
  authUser,
  onCreateSuccess,
  onUpdateSuccess,
}) => {
  const formikRef = useRef(null);
  const handleCancel = () => {
    closePopup();
  };

  const [confirmLoading, setConfirmLoading] = useState(false);

  const initialValues = {
    email: item?.email ?? "",
    fullName: item?.fullName ?? null,
    password: item?.password ?? null,
    isSuperAdmin: item?.isSuperAdmin ?? false,
  };

  const validationSchema = Yup.object({
    email: Yup.string().email().required("Email is required!"),
    password: !item
      ? Yup.string()
          .matches(/^(?![.\n])(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,20}$/, {
            message: "Password too weak",
          })
          .required("Password is required!")
      : Yup.string().nullable(),
    fullName: Yup.string().required("Name is required!"),
    isSuperAdmin: Yup.boolean(),
  });

  const addUser = (values) => {
    const formData = {
      email: values.email,
      fullName: values.fullName,
      password: values.password,
      isSuperAdmin: values.isSuperAdmin,
    };

    createUser(formData)
      .then(({ data }) => {
        setConfirmLoading(false);
        closePopup();
        notification.success({ message: "Successfully added user" });
        !!onCreateSuccess && onCreateSuccess();
      })
      .catch((error) => {
        setConfirmLoading(false);
        notification.error({ message: error.response.data.message });
      });
  };

  const updateUser = (values) => {
    const data = {
      email: values.email,
      fullName: values.fullName,
      isSuperAdmin: values.isSuperAdmin,
      id: item.id,
    };

    updateUserById(item.id, data)
      .then(({ data }) => {
        setConfirmLoading(false);
        closePopup();
        notification.success({ message: data.message });
        !!onUpdateSuccess && onUpdateSuccess();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  const onSubmit = (values) => {
    setConfirmLoading(true);

    if (item) {
      updateUser(values);
    } else {
      addUser(values);
    }
  };

  return (
    <>
      <Modal
        title={item ? "Edit User" : "Add User"}
        open={true}
        confirmLoading={confirmLoading}
        onOk={() => {
          formikRef.current.submitForm();
        }}
        width={768}
        onCancel={handleCancel}
      >
        <>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ errors, touched, handleChange, values, setFieldValue }) => {
              return (
                <Form>
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size={20}
                  >
                    <div>
                      <Title level={5}>Email *</Title>
                      <Input
                        placeholder="Enter email"
                        name="email"
                        value={values.email}
                        autoComplete="off"
                        onChange={handleChange}
                      />

                      {errors.email && touched.email ? (
                        <Text type="danger">{errors.email}</Text>
                      ) : null}
                    </div>

                    {!item && (
                      <div>
                        <Title level={5}>Password *</Title>
                        <Input
                          placeholder="Enter password"
                          name="password"
                          type="password"
                          value={values.password}
                          aria-autocomplete="none"
                          autoComplete="new-password"
                          onChange={handleChange}
                        />
                        <Text type="secondary">
                          Password must be 8 - 24 characters long and must
                          contain 1 uppercase, 1 lowercase and 1 number.
                        </Text>
                        {errors.password && touched.password ? (
                          <Text type="danger">{errors.password}</Text>
                        ) : null}
                      </div>
                    )}

                    <div>
                      <Title level={5}>Full Name *</Title>
                      <Input
                        placeholder="Enter full name"
                        name="fullName"
                        value={values.fullName}
                        onChange={handleChange}
                      />
                      {errors.fullName && touched.fullName ? (
                        <Text type="danger">{errors.fullName}</Text>
                      ) : null}
                    </div>

                    <div>
                      <Title level={5}>Is Super Admin?</Title>

                      <Checkbox
                        name="isSuperAdmin"
                        checked={values.isSuperAdmin}
                        onChange={(e) =>
                          setFieldValue("isSuperAdmin", e.target.checked)
                        }
                        disabled={item?.id === authUser.id}
                      >
                        Super Admin
                      </Checkbox>
                      <Alert
                        message={
                          item?.id === authUser.id
                            ? "You cannot change your own role."
                            : "Warning! Super admin role allows to edit and remove users."
                        }
                        type="error"
                        style={{ marginTop: "16px" }}
                      ></Alert>
                      {errors.isSuperAdmin && touched.isSuperAdmin ? (
                        <Text type="danger">{errors.isSuperAdmin}</Text>
                      ) : null}
                    </div>
                  </Space>
                </Form>
              );
            }}
          </Formik>
        </>
      </Modal>
    </>
  );
};

export default AddUserPopup;
